import {
  ArrowLeft, CheckCircle2, Download, Lock, Pencil, PlayCircle,
  Printer, RefreshCw, Save, Trash2, UserPlus,
} from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Header row with all action buttons of the filtered map detail page.
 * The "Confirmar mapa" button is replaced by a "confirmed" badge once the
 * map has been confirmed; admins see an unlock button next to it.
 */
export default function MapHeaderActions({
  user,
  confirmed,
  saving,
  confirming,
  dirty,
  onBack,
  onPrint,
  onExportPdf,
  onExportXlsx,
  onSaveRows,
  onAddManual,
  onEditMeta,
  onConfirmMap,
  onUnlockMap,
  onGenerateCases,
  onRegenerateCases,
  onDeleteMap,
}) {
  const isAdmin = user?.role === "admin" || user?.role === "admin_master";
  return (
    <div className="flex items-center justify-between flex-wrap gap-3 no-print">
      <Button variant="ghost" size="sm" onClick={onBack}>
        <ArrowLeft className="h-4 w-4 mr-1" /> Lista
      </Button>
      <div className="flex gap-2">
        <Button variant="outline" onClick={onPrint} data-testid="btn-print">
          <Printer className="h-4 w-4 mr-2" /> Imprimir
        </Button>
        <Button variant="outline" onClick={onExportPdf} data-testid="btn-export-map-pdf">
          <Download className="h-4 w-4 mr-2" /> PDF
        </Button>
        <Button variant="outline" onClick={onExportXlsx} data-testid="btn-export-map-xlsx">
          <Download className="h-4 w-4 mr-2" /> Excel
        </Button>
        <Button
          variant="outline"
          onClick={onSaveRows}
          disabled={saving}
          data-testid="btn-save-rows"
          className={dirty ? "border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100" : ""}
        >
          <Save className="h-4 w-4 mr-2" /> {dirty ? "Salvar edições •" : "Salvar edições"}
        </Button>
        <Button variant="outline" onClick={onAddManual} data-testid="btn-add-manual-row">
          <UserPlus className="h-4 w-4 mr-2" /> Adicionar linha
        </Button>
        <Button
          variant="outline"
          onClick={onEditMeta}
          data-testid="btn-edit-meta"
          title="Editar hospital, centro ou data do mapa"
        >
          <Pencil className="h-4 w-4 mr-2" /> Editar dados
        </Button>
        {!confirmed ? (
          <Button
            className="bg-emerald-700 hover:bg-emerald-800"
            onClick={onConfirmMap}
            disabled={confirming}
            data-testid="btn-confirm-map"
          >
            <CheckCircle2 className="h-4 w-4 mr-2" />
            {confirming ? "Confirmando..." : "Confirmar mapa"}
          </Button>
        ) : (
          <div
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-50 text-emerald-800 border border-emerald-200 text-xs font-semibold"
            data-testid="map-confirmed-badge"
          >
            <Lock className="h-3.5 w-3.5" /> Mapa confirmado
            {isAdmin && (
              <Button
                size="sm"
                variant="ghost"
                className="h-6 ml-1 px-2 text-emerald-800 hover:bg-emerald-100 text-xs"
                onClick={onUnlockMap}
                data-testid="btn-unlock-map"
                title="Destravar mapa para correção (apenas Admin)"
              >
                Destravar
              </Button>
            )}
          </div>
        )}
        <Button
          className="bg-blue-700 hover:bg-blue-800"
          onClick={onGenerateCases}
          data-testid="btn-generate-cases"
        >
          <PlayCircle className="h-4 w-4 mr-2" /> Gerar casos
        </Button>
        {(!confirmed || isAdmin) && (
          <Button
            variant="outline"
            className="text-violet-700 border-violet-300 hover:bg-violet-50"
            onClick={onRegenerateCases}
            data-testid="btn-regenerate-cases"
            title="Apaga todos os casos deste mapa, reaplica o filtro mais recente e recria os casos"
          >
            <RefreshCw className="h-4 w-4 mr-2" /> Regenerar casos
          </Button>
        )}
        <Button
          variant="outline"
          className="text-red-600 border-red-200 hover:bg-red-50"
          onClick={onDeleteMap}
          data-testid="btn-delete-map"
        >
          <Trash2 className="h-4 w-4 mr-2" /> Excluir mapa
        </Button>
      </div>
    </div>
  );
}
